import { FC, ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import ChevronLeftIcon from "../assets/chevron_left.svg?react";
import ChevronRightIcon from "../assets/chevron_right.svg?react";

interface PaginationProps {
  /** Current active page, starting at 1 */
  currentPage: number;

  /** Total number of items to paginate */
  itemCount: number;

  /** Number of items per page. Defaults to 10 */
  pageSize?: number;

  /** Callback for changing page */
  setCurrentPage: (page: number) => void;
}

type PageItem = number | "...";

const PaginationWrapper = styled.ul`
  margin: 30px 0;
  padding: 0 25px;
  list-style: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
`;

const PaginationItem = styled.li`
  min-width: 50px;
  height: 50px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  text-align: center;
  flex: 0 0 auto;
  cursor: pointer;
  user-select: none;

  & svg {
    fill: ${p => p.theme.textColor};
  }

  &.active {
    background-color: ${p => p.theme.accentColor};
  }

  &:active {
    background-color: ${p => p.theme.accentColor}50;
  }

  &.disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const EllipsisItem = styled.li`
  min-width: 28px;
  height: 40px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  user-select: none;
`;

/**
 * Builds a compact list of page numbers.
 *
 * Always shows:
 * - first page
 * - last page
 * - current page
 * - one page before current
 * - one page after current
 *
 * Inserts "..." where pages are skipped.
 *
 * Example:
 * currentPage = 7, pageCount = 20
 * returns: [1, "...", 6, 7, 8, "...", 20]
 */
const getPaginationItems = (
  currentPage: number,
  pageCount: number
): PageItem[] => {
  const visiblePages = new Set<number>();

  visiblePages.add(1);
  visiblePages.add(pageCount);

  for (let page = currentPage - 1; page <= currentPage + 1; page++) {
    if (page > 1 && page < pageCount) {
      visiblePages.add(page);
    }
  }

  const sortedPages = [...visiblePages].sort((a, b) => a - b);
  const items: PageItem[] = [];

  sortedPages.forEach((page, index) => {
    const previousPage = sortedPages[index - 1];

    if (previousPage && page - previousPage > 1) {
      items.push("...");
    }

    items.push(page);
  });

  return items;
};

/**
 * Render a compact pagination component.
 *
 * The component avoids rendering every single page number when there are many
 * pages. Instead, it shows a small window around the current page, plus the
 * first and last page.
 */
const Pagination: FC<PaginationProps> = ({
  currentPage,
  itemCount,
  pageSize = 10,
  setCurrentPage
}) => {
  const pageCount = Math.ceil(itemCount / pageSize);

  const paginationItems: ReactElement[] = useMemo(() => {
    return getPaginationItems(currentPage, pageCount).map((item, index) => {
      if (item === "...") {
        return (
          <EllipsisItem key={`pagination-ellipsis-${index}`}>
            …
          </EllipsisItem>
        );
      }

      return (
        <PaginationItem
          key={`pagination-${item}`}
          className={item === currentPage ? "active" : undefined}
          onClick={() => setCurrentPage(item)}
        >
          {item}
        </PaginationItem>
      );
    });
  }, [currentPage, pageCount, setCurrentPage]);

  if (pageCount <= 1) {
    return null;
  }

  return (
    <PaginationWrapper className="showInPreview">
      {currentPage > 1 && (
        <PaginationItem onClick={() => setCurrentPage(currentPage - 1)}>
          <ChevronLeftIcon />
        </PaginationItem>
      )}

      {paginationItems}

      {currentPage < pageCount && (
        <PaginationItem onClick={() => setCurrentPage(currentPage + 1)}>
          <ChevronRightIcon />
        </PaginationItem>
      )}
    </PaginationWrapper>
  );
};

export default Pagination;